import { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';

import moment from 'moment-timezone';
import { Label, Row, Col, Button, Badge, Separator, DefaultTooltip } from 'twyne-new.ui';

import { ReportBody } from '@/components/reports/body';
import appStore from '@Stores/app';
import InfoTip from 'components/InfoTip';

import sessionStore from '../../stores/session';

import DetailsCard from './components/DetailsCard';
import Header from './components/Header';
import Histogram from './components/Histogram';
import ProgressCard from './components/ProgressCard';
import SimpleCard from './components/SimpleCard';
import TotalsCard from './components/TotalsCard';
import infoTips from './components/info-tips';

function ProgressCardWithTips({ data, status, title, field, infotip, onAddFilter }) {
  const { header: headerTip, items: withTip } = infoTips[infotip] || {}
  return <ProgressCard
    withTip={withTip}
    status={status}
    data={data}
    field={field}
    title={<>
      <Label>
        {title}
        {headerTip ? <InfoTip id={field} color='neutral'>
          {headerTip}
        </InfoTip> : null}
      </Label>
    </>}
    onAddFilter={onAddFilter}
  />
}

export default function SessionLayout() {
  const [data, setData] = useState({})
  const [params, setParams] = useState({
    startDate: moment.tz(appStore.timezone).startOf('day'),
    endDate: moment.tz(appStore.timezone).endOf('day'),
    sideId: null,
    filters: {},
    conversiontype: "conversion",
  })
  const [filters, setFilters] = useState({})
  const [filtersApplied, setFiltersApplied] = useState(true)

  const loadData = useCallback(async () => {
    setData({})
    sessionStore.getData(params).then(async response => {
      await sessionStore.post.fetchData();
      setData(response)
    });
  }, [params])

  useEffect(() => {
    loadData()
  }, [loadData])

  function handleChangeDates({ startDate, endDate }) {
    setParams(prevParams => ({ ...prevParams, startDate, endDate }))
  }

  function handleAddFilter(field, value) {
    setFilters(prevFilters => {
      if (prevFilters[field]?.includes(value)) return prevFilters

      prevFilters[field] ? prevFilters[field].push(value) : prevFilters[field] = [value]
      return { ...prevFilters }
    })

    setFiltersApplied(false)
  }

  function handleApplyFilters() {
    setParams(prevParams => ({ ...prevParams, filters }))
    setFiltersApplied(true)
  }

  function handlePopFilter(field, index) {
    setFilters(prevFilters => {
      prevFilters[field]?.splice(index, 1)
      if (prevFilters[field]?.length === 0) delete prevFilters[field]
      return { ...prevFilters }
    })

    setFiltersApplied(false)
  }

  function handleChangeHistogram(conversiontype) {
    setParams(prevParams => ({ ...prevParams, conversiontype }))
  }

  function getUrlLink(id, field) {
    if (field === 'lander' || field === 'postTarget') {
      const { post: { objectsMapping } } = sessionStore;
      if (objectsMapping[id]) return `/cms/${objectsMapping[id].content_type}/${id}`;

      return '/cms';
    }

    if (field === 'publisher') return `/offer/publishers/${id}`;
    if (field === 'allocation') return `/offer/allocations/${id}`;
    if (field === 'form') return `/offer/forms/${id}/fields`;

    return '/'
  }

  function labelFormatting(field, label, id) {
    const fields = [
      'publusher',
      'allocation',
      'form',
      'lander',
      'postTarget',
    ];
    return (
      fields.includes(field) ? <Link to={getUrlLink(id, field)} className="text-secondary" >
        {label}
      </Link> : label
    );
  }

  const Filters = () => {
    const [filtersArray, setFiltersArray] = useState([])

    useEffect(() => {
      setFiltersArray(Object.entries(filters))
    }, [])

    if (!filtersArray.length) return null

    return <>
      <div className='flex justify-between items-center'>
        <div className='flex gap-4 items-center'>
          <span className='text-md twy-text-base-accent'>Filters:</span>
          <div className='flex gap-2 items-center'>
            {filtersArray.map(([key, value], keyIndex) => value.map((id, valueIndex) => <>
              <Badge
                variant='light'
                size='lg'
                className='cursor-pointer'
                key={keyIndex}
                onClick={() => handlePopFilter(key, valueIndex)}
              >
                {key}: {id}
              </Badge>
            </>))}
          </div>
        </div>

        <Button variant='light' color='accent' size='sm' onClick={() => handleApplyFilters()}>
          Apply Filters
        </Button>
      </div>
      <Separator orientation='horizontal' className='my-4 mx-0' />
    </>
  }

  return <>
    <Helmet title="Session" />
    <Header
      dates={params}
      handleChangeDates={handleChangeDates}
      handleChangeDatesTime={(e, obj) => handleChangeDates(obj)}
      sync={loadData}
      isLoading={sessionStore.isLoading}
    />
    <ReportBody contentClassName='p-4'>
      <Filters />
      <Row>
        <Col size='12'>
          <Histogram
            onChangeHistogram={handleChangeHistogram}
            data={data.histogram}
            conversiontype={params.conversiontype}
            status={sessionStore.status}
          />
        </Col>
        <Col size='3' className='lg:col-span-2'>
          <TotalsCard
            status={sessionStore.status}
            data={data.registrationCompleted}
            total={data.total}
            header="Completed"
            onAddFilter={value => {
              if (!filters['registrationCompleted']) handleAddFilter('registrationCompleted', value)
            }}
            field="registrationCompleted"
          />
        </Col>
        <Col size='3' className='lg:col-span-2'>
          <TotalsCard
            status={sessionStore.status}
            data={data.uniqUsers}
            total={data.registrationCompleted?.total}
            onAddFilter={value => {
              if (!filters['uniqUsers']) handleAddFilter('uniqUsers', value)
            }}
            field="uniqUsers"
            header="Unique Users"
          />
        </Col>
        <Col size='3' className='lg:col-span-2'>
          <TotalsCard
            status={sessionStore.status}
            data={data.tcpaConsent}
            total={data.registrationCompleted?.total}
            header="TCPA Consent"
            onAddFilter={value => {
              if (!filters['tcpaConsent']) handleAddFilter('tcpaConsent', value)
            }}
            field="tcpaConsent"
          />
        </Col>
        <Col size='3' className='lg:col-span-2'>
          <TotalsCard
            status={sessionStore.status}
            data={data.duplicated}
            total={data.total}
            header="Duplicated"
            onAddFilter={value => {
              if (!filters['duplicated']) handleAddFilter('duplicated', value)
            }}
            field="duplicated"
          />
        </Col>
        <Col size='6' className='lg:col-span-2'>
          <TotalsCard
            status={sessionStore.status}
            data={data.duplicatedData}
            total={data.total}
            header="Duplicated Data"
            onAddFilter={value => {
              if (!filters['duplicatedData']) handleAddFilter('duplicatedData', value)
            }}
            field="duplicatedData"
          />
        </Col>
        <Col size='6' className='lg:col-span-2'>
          <TotalsCard
            status={sessionStore.status}
            data={data.duplicatedClick}
            total={data.total}
            header="Duplicated Click"
            onAddFilter={value => {
              if (!filters['duplicatedClick']) handleAddFilter('duplicatedClick', value)
            }}
            field="duplicatedClick"
          />
        </Col>
        <Col size='3'>
          <SimpleCard
            status={sessionStore.status}
            data={data.total}
            title="Impressions"
          />
        </Col>
        <Col size='3'>
          <SimpleCard
            isMoney
            status={sessionStore.status}
            data={data.revenue}
            title="Revenue"
          />
        </Col>
        <Col size='3'>
          <SimpleCard
            isMoney
            status={sessionStore.status}
            data={data.cost}
            title="Costs"
          />
        </Col>
        <Col size='3'>
          <SimpleCard
            isMoney
            status={sessionStore.status}
            data={data.cost}
            total={data.revenue}
            title="Margin"
          />
        </Col>
        <Col size='12' className='lg:col-span-6'>
          <ProgressCardWithTips
            onAddFilter={value => handleAddFilter('conversion', value)}
            status={sessionStore.status}
            total={data.total}
            data={data.conversion}
            field="conversion"
            title="Conversion Type"
            infotip="conversion"
          />
        </Col>
        <Col size='12' className='lg:col-span-6'>
          <ProgressCardWithTips
            total={data.total}
            status={sessionStore.status}
            onAddFilter={value => handleAddFilter('payout', value)}
            data={data.payout}
            field="payouttype"
            title="Payout Type"
          />
        </Col>
        <Col size='6' className='lg:col-span-4'>
          <ProgressCardWithTips
            total={data.total}
            status={sessionStore.status}
            onAddFilter={value => handleAddFilter('botVerification', value)}
            data={data.botVerification}
            field="bot_verification"
            title="Bot Verification"
            infotip="botVerification"
          />
        </Col>
        <Col size='6' className='lg:col-span-4'>
          <ProgressCardWithTips
            total={data.total}
            status={sessionStore.status}
            onAddFilter={value => handleAddFilter('emailVerification', value)}
            data={data.emailVerification}
            field="email_verification"
            title="Email Verification"
            infotip="emailVerification"
          />
        </Col>
        <Col size='6' className='lg:col-span-4'>
          <ProgressCardWithTips
            total={data.total}
            status={sessionStore.status}
            onAddFilter={value => handleAddFilter('postalVerification', value)}
            data={data.postalVerification}
            field="postal_verification"
            title="Postal Verification"
            infotip="postalVerification"
          />
        </Col>
        <Col size='6' className='lg:col-span-4'>
          <ProgressCardWithTips
            total={data.total}
            status={sessionStore.status}
            onAddFilter={value => handleAddFilter('phoneVerification', value)}
            data={data.phoneVerification}
            field="phone_verification"
            title="Phone Verification"
            infotip="phoneVerification"
          />
        </Col>
        <Col size='12' className='lg:col-span-4'>
          <ProgressCardWithTips
            total={data.total}
            status={sessionStore.status}
            onAddFilter={value => handleAddFilter('fraudVerification', value)}
            data={data.fraudVerification}
            field="fraud_verification"
            title="Fraud Verification"
            infotip="fraudVerification"
          />
        </Col>
        <Col size='12' className='lg:col-span-4'>
          <ProgressCardWithTips
            total={data.total}
            onAddFilter={value => handleAddFilter('fraudScore', value)}
            data={data.fraudScore}
            field="fraudScore"
            title="Fraud Score"
            infotip="fraudScore"
          />
        </Col>
        <Col className='columns-2 lg:columns-3 gap-4'>
          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.publisher}
            field="publisher"
            labelFormatting={labelFormatting}
            header="Publisher"
            columns={["Publisher", "Hits"]}
            onAddFilter={value => handleAddFilter('publisher', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            data={data.allocation}
            total={data.total}
            field="allocation"
            labelFormatting={labelFormatting}
            header="Allocation"
            columns={["Allocation", "Hits"]}
            onAddFilter={value => handleAddFilter('allocation', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.form}
            field="form"
            labelFormatting={labelFormatting}
            header="Form"
            columns={["Form", "Hits"]}
            onAddFilter={value => handleAddFilter('form', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.lander}
            field="lander"
            labelFormatting={labelFormatting}
            header="Lander"
            columns={["Lander", "Hits"]}
            onAddFilter={value => handleAddFilter('lander', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            data={data.postVersion}
            total={data.total}
            field="postVersion"
            labelFormatting={labelFormatting}
            header="Lander / Version"
            labeled={({ name, key, postTitle, postId }, id) => <>
              <DefaultTooltip content={`ID: ${postId}`}>
                <span id={`${id}-post`}>
                  {postTitle}
                </span>
              </DefaultTooltip>
              <DefaultTooltip content={`ID: ${key}`}>
                <i id={`${id}-version`}>
                  (version "<b>{name}</b>")
                </i>
              </DefaultTooltip>
            </>}
            columns={["Version", "Hits"]}
            onAddFilter={value => handleAddFilter('postVersion', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            data={data.postTarget}
            total={data.total}
            field="postTarget"
            labelFormatting={labelFormatting}
            header="Target post"
            columns={["Target post", "Hits"]}
            onAddFilter={value => handleAddFilter('postTarget', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.browser}
            field="browser"
            labelFormatting={labelFormatting}
            header="Browser"
            columns={["Browser", "Hits"]}
            limit={5}
            onAddFilter={value => handleAddFilter('browser', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            data={data.device}
            total={data.total}
            field="device"
            labelFormatting={labelFormatting}
            header="Device"
            columns={["Device", "Hits"]}
            onAddFilter={value => handleAddFilter('device', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            data={data.userState}
            total={data.total}
            field="userState"
            labelFormatting={labelFormatting}
            header="User State"
            columns={["User State", "Hits"]}
            limit={5}
            onAddFilter={value => handleAddFilter('userState', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.userGender}
            field="userGender"
            labelFormatting={labelFormatting}
            header="User Gender"
            columns={["User Gender", "Hits"]}
            onAddFilter={value => handleAddFilter('userGender', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.registrationStep}
            field="registrationStep"
            labelFormatting={labelFormatting}
            header="Registration Step abandonment"
            columns={["Step", "Hits"]}
            onAddFilter={value => handleAddFilter('registrationStep', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.userAge}
            field="userAge"
            labelFormatting={labelFormatting}
            header="User Age"
            columns={["User Age", "Hits"]}
            onAddFilter={value => handleAddFilter('userAge', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.dupeDaysDelta}
            field="dupeDaysDelta"
            labelFormatting={labelFormatting}
            header="Dupe Days Delta"
            columns={["Dupe Days Delta", "Hits"]}
            onAddFilter={value => handleAddFilter('dupeDaysDelta', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.userCity}
            field="userCity"
            labelFormatting={labelFormatting}
            header="User City"
            columns={["User City", "Hits"]}
            limit={5}
            onAddFilter={value => handleAddFilter('userCity', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.sid1}
            field="sid1"
            labelFormatting={labelFormatting}
            header="Sid 1"
            columns={["Sid 1", "Hits"]}
            limit={5}
            onAddFilter={value => handleAddFilter('sid1', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.os}
            field="os"
            labelFormatting={labelFormatting}
            header="OS"
            columns={["OS", "Hits"]}
            onAddFilter={value => handleAddFilter('os', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
          />

          <DetailsCard
            filters={filters}
            status={sessionStore.status}
            total={data.total}
            data={data.emailDomain}
            field="emailDomain"
            labelFormatting={labelFormatting}
            header="Email Domain"
            columns={["Domain", "Hits"]}
            onAddFilter={value => handleAddFilter('emailDomain', value)}
            onApplyFilters={handleApplyFilters}
            filtersApplied={filtersApplied}
            limit={5}
          />
        </Col>
      </Row>
    </ReportBody>
  </>
}
